import { Component, DestroyRef, effect, inject, input, linkedSignal, model, output, signal, untracked } from '@angular/core';
import { FormsModule } from '@angular/forms';
import { HttpErrorResponse } from '@angular/common/http';
import { MatButtonModule } from '@angular/material/button';
import { MatIconModule } from '@angular/material/icon';
import { finalize } from 'rxjs';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { TranslocoPipe, TranslocoService } from '@jsverse/transloco';
import { ConfigurationWaterfall, ConfigurationWaterfallService } from '../configuration-waterfall.service';
import { DiffView } from '../diff-view/diff-view';
import { PromptEditor } from '../prompt-editor/prompt-editor';
import {
  DiffLine, Environment, Notice, PayloadKind, RenderMode, TextFn,
  diffLines, editKind, editorText, environmentLabel, environments, fromEditorJson, lineDigits as lineDigitsFor, lineNumbers as lineNumbersFor, payload, presentDraft, promptEditorText,
} from '../configuration-view';

@Component({
  selector: 'app-editor-drawer',
  imports: [FormsModule, MatButtonModule, MatIconModule, TranslocoPipe, DiffView, PromptEditor],
  templateUrl: './editor-drawer.html',
  styleUrl: './editor-drawer.scss',
})
export class EditorDrawer {
  private readonly api = inject(ConfigurationWaterfallService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly transloco = inject(TranslocoService);
  readonly row = input.required<ConfigurationWaterfall>();
  readonly rightRow = input<ConfigurationWaterfall | null>(null);
  readonly leftEnvironment = input.required<Environment>();
  readonly rightEnvironment = input.required<Environment>();
  readonly lockedRight = input<string | null>(null);
  readonly lockedRightLabel = input<string | null>(null);
  readonly renderMode = model<RenderMode>('rendered');
  readonly closed = output<void>();
  readonly saved = output<ConfigurationWaterfall>();

  readonly leftDraft = signal('');
  readonly rightDraft = signal('');
  readonly savingSide = signal<'left' | 'right' | null>(null);
  readonly pendingSide = signal<'left' | 'right' | null>(null);
  readonly editorError = signal<Notice | null>(null);
  readonly leftDetail = signal(false);
  readonly rightDetail = signal(false);
  readonly leftSync = signal(0);
  readonly rightSync = signal(0);
  readonly environments = environments;
  readonly chosenLeft = linkedSignal(() => this.leftEnvironment());
  readonly chosenRight = linkedSignal(() => this.rightEnvironment());
  private keepDraft: 'left' | 'right' | null = null;
  private readonly text: TextFn = key => this.transloco.translate(key);

  constructor() {
    effect(() => {
      const leftRow = this.row();
      const rightRow = this.rightRow() ?? leftRow;
      const left = this.chosenLeft();
      const right = this.chosenRight();
      const leftKind = editKind(leftRow);
      const rightKind = editKind(rightRow);
      const locked = this.lockedRight();
      const keep = this.keepDraft;
      this.keepDraft = null;
      if (keep !== 'left') this.replaceDraft('left', payload(leftRow, left, leftKind), leftKind);
      if (keep !== 'right') this.replaceDraft('right', locked !== null ? locked : payload(rightRow, right, rightKind), rightKind);
    });
  }

  sideRow(side: 'left' | 'right'): ConfigurationWaterfall {
    return side === 'right' ? (this.rightRow() ?? this.row()) : this.row();
  }

  sideLabel(side: 'left' | 'right'): string {
    if (side === 'right' && this.lockedRightLabel()) return this.lockedRightLabel()!;
    if (this.rightRow()) {
      return this.sideRow(side).swaggerName || this.text('table.noSpecification');
    }
    return environmentLabel(side === 'left' ? this.chosenLeft() : this.chosenRight());
  }

  editLabel(side: 'left' | 'right' = 'left'): string {
    return editKind(this.sideRow(side)) === 'ApiCore' ? this.text('fields.apiCoreData') : this.text('fields.applicationData');
  }

  promptSide(side: 'left' | 'right'): boolean {
    return editKind(this.sideRow(side)) === 'ApiCore';
  }

  locked(side: 'left' | 'right'): boolean {
    return side === 'right' && this.lockedRight() !== null;
  }

  lineNumbers(value: string): string {
    return lineNumbersFor(value);
  }

  lineDigits(value: string): number {
    return lineDigitsFor(value);
  }

  syncGutter(gutter: HTMLElement, area: HTMLTextAreaElement): void {
    const align = () => { gutter.scrollTop = area.scrollTop; };
    align();
    requestAnimationFrame(align);
  }

  draftLines(side: 'left' | 'right'): DiffLine[] {
    const leftJson = this.sideRow('left').type === 'api-core-configuration';
    const rightJson = this.sideRow('right').type === 'api-core-configuration';
    return diffLines(presentDraft(this.leftDraft(), leftJson, this.renderMode()), presentDraft(this.rightDraft(), rightJson, this.renderMode()))[side];
  }

  chooseEnvironment(side: 'left' | 'right', environment: Environment): void {
    if (side === 'right' && this.locked('right')) return;
    if (side === 'left' && !this.locked('right') && environment === this.chosenRight()) return;
    if (side === 'right' && environment === this.chosenLeft()) return;
    if (side === 'left') this.chosenLeft.set(environment);
    else this.chosenRight.set(environment);
  }

  swapEnvironments(): void {
    if (this.locked('right')) return;
    const left = this.chosenLeft();
    this.chosenLeft.set(this.chosenRight());
    this.chosenRight.set(left);
  }

  copyToLeft(): void {
    this.leftDraft.set(this.rightDraft());
    this.leftSync.update(value => value + 1);
  }

  copyToRight(): void {
    if (this.locked('right')) return;
    this.rightDraft.set(this.leftDraft());
    this.rightSync.update(value => value + 1);
  }

  resetChanges(): void {
    if (this.savingSide()) return;
    const leftKind = editKind(this.sideRow('left'));
    const rightKind = editKind(this.sideRow('right'));
    const locked = this.lockedRight();
    this.replaceDraft('left', payload(this.sideRow('left'), this.chosenLeft(), leftKind), leftKind, true);
    this.replaceDraft('right', locked !== null ? locked : payload(this.sideRow('right'), this.chosenRight(), rightKind), rightKind, true);
    this.editorError.set(null);
  }

  save(side: 'left' | 'right'): void {
    if (this.locked(side) || this.savingSide() || this.pendingSide()) return;
    if (this.preparedData(side) === null) return;
    this.pendingSide.set(side);
  }

  cancelSave(): void {
    if (this.savingSide()) return;
    this.pendingSide.set(null);
  }

  confirmSave(): void {
    const side = this.pendingSide();
    if (!side || this.savingSide()) return;
    const data = this.preparedData(side);
    this.pendingSide.set(null);
    if (data === null) return;
    const row = this.sideRow(side);
    const source = side === 'left' ? this.chosenLeft() : this.chosenRight();
    this.savingSide.set(side);
    this.editorError.set(null);
    this.api.update(row, source, data).pipe(takeUntilDestroyed(this.destroyRef), finalize(() => this.savingSide.set(null))).subscribe({
      next: updated => {
        this.keepDraft = side === 'left' ? 'right' : 'left';
        this.saved.emit(updated);
      },
      error: (error: HttpErrorResponse) => this.editorError.set(this.saveError(error)),
    });
  }

  private replaceDraft(side: 'left' | 'right', value: string | null, kind: PayloadKind, force = false): void {
    const text = kind === 'ApiCore' ? promptEditorText(value) : editorText(value, kind);
    const current = untracked(() => side === 'left' ? this.leftDraft() : this.rightDraft());
    if (side === 'left') this.leftDraft.set(text);
    else this.rightDraft.set(text);
    if (!force && current === text) return;
    if (side === 'left') this.leftSync.update(version => version + 1);
    else this.rightSync.update(version => version + 1);
  }

  private preparedData(side: 'left' | 'right'): string | null {
    const kind = editKind(this.sideRow(side));
    let data = side === 'left' ? this.leftDraft() : this.rightDraft();
    if (kind === 'ApiCore') {
      try { data = fromEditorJson(data); }
      catch {
        this.editorError.set({ kind: 'key', key: 'errors.invalidJson' });
        return null;
      }
    }
    return data;
  }

  private saveError(error: HttpErrorResponse): Notice {
    if (typeof error.error === 'string' && error.error.trim()) return { kind: 'text', text: error.error };
    if (error.status === 404) return { kind: 'key', key: 'errors.notFound' };
    return { kind: 'key', key: 'errors.save' };
  }
}
